import React from 'react';
import classnames from 'classnames';

import { useTranslation } from 'src/i18n';
import { OrganizationEmploymentGroup } from 'utils/employment/types';
import { formatPeriodRange } from 'utils/employment/formatters';

import styles from './BiographyEmploymentTimeline.module.scss';

export interface ReadOnlyOrganizationGroupProps {
  /** Organization employment group data */
  group: OrganizationEmploymentGroup;
  /** Timeline content to render inside the group */
  children: React.ReactNode;
  /** Whether this is the first group (affects top margin) */
  isFirst?: boolean;
  /** Whether this is the last group (affects bottom margin) */
  isLast?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

const ReadOnlyOrganizationGroup: React.FC<ReadOnlyOrganizationGroupProps> = ({
  group,
  children,
  isFirst = false,
  isLast = false,
  className,
  testId,
}) => {
  const { t } = useTranslation('NS_EDIT');
  const { organization, period } = group;

  // State for image loading
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  // Generate organization avatar fallback (first letter of name)
  const getAvatarFallback = React.useCallback((name: string): string => {
    return name.charAt(0).toUpperCase();
  }, []);

  // Get organization image URL (prefer medium size for headers)
  const getOrganizationImageUrl = React.useCallback(() => {
    // Handle different possible image data structures
    if (organization.image) {
      // Standard structure: { medium, small, original }
      if (typeof organization.image === 'object') {
        return organization.image.medium || organization.image.small || organization.image.original;
      }
      // Direct URL string
      if (typeof organization.image === 'string') {
        return organization.image;
      }
    }
    
    // Check for alternative image fields
    if (organization.logo) {
      if (typeof organization.logo === 'object') {
        return organization.logo.medium || organization.logo.small || organization.logo.original;
      }
      if (typeof organization.logo === 'string') {
        return organization.logo;
      }
    }
    
    return undefined;
  }, [organization.image, organization.logo, organization]);

  // Format the period display text
  const periodText = React.useMemo(() => formatPeriodRange(period, t), [period, t]);

  const imageUrl = getOrganizationImageUrl();

  // Reset image state when imageUrl changes
  React.useEffect(() => {
    if (imageUrl) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [imageUrl]);

  return (
    <div
      className={classnames(styles.organizationGroup, className)}
      data-testid={testId}
    >
      {/* Organization Header */}
      <div className={styles.organizationHeader}>
        {/* Organization Avatar */}
        <div className={styles.organizationAvatar}>
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={organization.name}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <span className={styles.organizationAvatarFallback}>
              {getAvatarFallback(organization.name)}
            </span>
          )}
        </div>

        {/* Organization Info */}
        <div className={styles.organizationInfo}>
          <h3 className={styles.organizationName}>
            {organization.name}
          </h3>
          <p className={styles.organizationPeriod}>
            {periodText}
          </p>
          {(organization.country || organization.organizationType) && (
            <div className={styles.organizationDetails}>
              {organization.country && (
                <span>{organization.country.name}</span>
              )}
              {organization.organizationType && (
                <span>{organization.organizationType.name}</span>
              )}
            </div>
          )}
        </div>


      </div>

      {/* Timeline Content */}
      <div className={styles.employmentTimeline}>
        {children}
      </div>
    </div>
  );
};

export default ReadOnlyOrganizationGroup; 